/**
 * App Store - Refrakt-based store for the main app
 *
 * This store manages top-level app state:
 * - Session/auth state
 * - Current view/routing
 * - Project data (list, current project info)
 * - Forces data
 * - UI state (nav visibility, etc.)
 * @module
 */
import { pipe, type Store, store, updateUnknown } from "refrakt";
import { fx } from "refrakt/middleware/fx.js";
import { logger } from "refrakt/middleware/logger.js";
import * as AiChat from "./app/ai-chat-store.ts";
import { filterMap, flatMapAsync } from "@gordonb/generator";
import * as Result from "@gordonb/result/result";
import * as Logger from "../services/logger.ts";
import { type Session, supabase } from "../services/supabase.ts";
import { type Ulid } from "../../models/ulid.ts";
import { type Slug } from "../../models/slug.ts";
import { type Note } from "../../models/note.ts";
import {
  type ForceNote,
  CoerceForceNoteSchema,
} from "../../models/note/force.ts";
import {
  type ForceConnectionNote,
  CoerceForceConnectionNoteSchema,
} from "../../models/note/force-connection.ts";
import {
  type LibraryNote,
  type LibraryNoteItem,
  type LibraryNoteTeaser,
} from "../../models/library-note.ts";
import { type ProjectInfo, type ProjectItem } from "../../models/project.ts";
import { type Conversation } from "../../models/conversation.ts";
import * as Config from "../client-config.ts";
import { router } from "../services/router.ts";
import {
  deleteProject,
  getProject,
  getProjects,
  upsertProject,
} from "../services/project.ts";
import {
  createConversation,
  deleteConversation,
  listProjectConversations,
} from "../services/conversations.ts";
import * as NotesService from "../services/notes.ts";
import * as LibraryNotesService from "../services/library-notes.ts";
import { dedupeById } from "../../util/identifiable.ts";
import { toMapBySlug, updateMapBySlug } from "../../util/sluggable.ts";
import { ulid } from "@std/ulid";
import { type SessionResult } from "../services/supabase.ts";
import {
  type ChatMessage,
  parseUserChatMessage,
} from "../../models/chat-message.ts";
import { nowIsoString } from "../../util/date.ts";
import { type ScenarioPhase } from "../../tools/state/schema.ts";
import { sleep } from "../../util/sleep.ts";

const log = Logger.create({ source: "client/app" });

/** Core views routed via URL */
export type AppViewKey =
  | "home"
  | "library"
  | "library-note"
  | "projects"
  | "project"
  | "project-conversation";

/** Ephemeral canvas views shown in conversation */
export type CanvasViewKey =
  | "project-info"
  | "force-table"
  | "force-vis"
  | "force-map"
  | "force-edit"
  | "report"
  | "scan-hits";

export type ViewHomeAction = {
  type: "view-home";
  session: SessionResult;
};

export type ViewProjectAction = {
  type: "view-project";
  projectId: Ulid | undefined;
  session: SessionResult;
};

export type ViewProjectConversationAction = {
  type: "view-project-conversation";
  projectId: Ulid;
  conversationId: Ulid;
  session: SessionResult;
  /** Whether to load messages from API (default: true) */
  loadMessages?: boolean;
};

export type ViewLibraryAction = {
  type: "view-library";
  session: SessionResult;
};

export type ViewLibraryNoteAction = {
  type: "view-library-note";
  slug: Slug;
  session: SessionResult;
};

export type SetLibraryNoteViewAction = {
  type: "set-library-note-view";
  slug: Slug;
};

export type LoadLibraryNoteAction = {
  type: "load-library-note";
  slug: Slug;
};

export type SetLibraryNoteAction = {
  type: "set-library-note";
  value: LibraryNote | undefined;
};

export type SetLibraryNoteErrorAction = {
  type: "set-library-note-error";
  error: string;
};

export type SetProjectInfoAction = {
  type: "set-project-info";
  value: ProjectInfo;
};

export type SetProjectTitleAction = {
  type: "set-project-title";
  value: string;
};

export type SetProjectDescriptionAction = {
  type: "set-project-description";
  value: string;
};

export type SetProjectFocusAction = {
  type: "set-project-focus";
  value: string;
};

export type ScheduleProjectSaveAction = {
  type: "schedule-project-save";
};

export type ProjectSaveAction = {
  type: "project-save";
  value: number;
};

export type ProjectSaveSuccessAction = {
  type: "project-save-success";
};

export type SetHomeViewAction = {
  type: "set-home-view";
};

export type SetLibraryViewAction = {
  type: "set-library-view";
};

export type SetProjectViewAction = {
  type: "set-project-view";
  projectId: Ulid;
};

export type SetProjectConversationViewAction = {
  type: "set-project-conversation-view";
  projectId: Ulid;
  conversationId: Ulid;
};

export type SetCanvasViewAction = {
  type: "set-canvas-view";
  canvasView: CanvasViewKey | undefined;
  slug: Slug | undefined;
  toolCallId?: string;
};

export type SetScenarioPhaseAction = {
  type: "set-scenario-phase";
  value: ScenarioPhase | undefined;
};

export type ToggleAppPanelAction = {
  type: "toggle-app-panel";
  value: boolean;
};

export type SetSidebarOpenAction = {
  type: "set-sidebar-open";
  value: boolean;
};

export type ToggleCreateProjectModalAction = {
  type: "toggle-create-project-modal";
  value: boolean;
};

export type SetCreateProjectModalTitleAction = {
  type: "set-create-project-modal-title";
  value: string;
};

export type SetCreateProjectModalDescriptionAction = {
  type: "set-create-project-modal-description";
  value: string;
};

export type SubmitCreateProjectModal = {
  type: "submit-create-project-modal";
  id: Ulid;
  title: string;
  description: string;
  accessToken: string | undefined;
};

export type ToggleNoteEditModalAction = {
  type: "toggle-note-edit-modal";
  value: boolean;
};

export type EscKeyAction = {
  type: "esc-key";
};

export type ForceEditSaveSuccessAction = {
  type: "force-edit-save-success";
  value: Ulid;
};

export type DeleteNoteAction = {
  type: "delete-note";
  slug: Slug;
  projectId: Ulid;
  redirectTo?: string;
  accessToken: string | undefined;
};

export type CreateConversationAction = {
  type: "create-conversation";
  message: string;
};

export type CreateConversationSuccessAction = {
  type: "create-conversation-success";
  projectId: Ulid;
  conversationId: Ulid;
  agentId: Ulid;
  title: string;
  createdAt: string;
  updatedAt: string;
  message: string;
};

export type ReloadProjectListAction = { type: "reload-project-list" };

export type SetProjectListAction = {
  type: "set-project-list";
  value: ProjectItem[];
};

export type CreateProjectAction = {
  type: "create-project";
  info: ProjectInfo;
};

export type CreateProjectSuccessAction = {
  type: "create-project-success";
  info: ProjectInfo;
};

export type LoadProjectAction = {
  type: "load-project";
  id: Ulid | undefined;
  accessToken: string | undefined;
  force: boolean;
};

export type LoadConversationAction = {
  type: "load-conversation";
  value: Ulid | undefined;
  accessToken: string | undefined;
};

export type LoadConversationsAction = {
  type: "load-conversations";
  projectId: Ulid | undefined;
  accessToken: string | undefined;
};

export type SetConversationsAction = {
  type: "set-conversations";
  value: Conversation[];
};

export type DeleteConversationAction = {
  type: "delete-conversation";
  value: Ulid;
};

export type DeleteConversationSuccessAction = {
  type: "delete-conversation-success";
  value: Ulid;
};

export type ToggleDeleteProjectAlertAction = {
  type: "toggle-delete-project-alert";
  show: boolean;
};

export type DeleteProjectAction = {
  type: "delete-project";
  value: Ulid;
};

export type DeleteProjectSuccessAction = {
  type: "delete-project-success";
  value: Ulid;
};

export type LoadForcesAction = { type: "load-forces" };
export type SetForcesAction = { type: "set-forces"; value: ForceNote[] };
export type SetForceConnectionsAction = {
  type: "set-force-connections";
  value: ForceConnectionNote[];
};
export type AppendForcesAction = { type: "append-forces"; value: ForceNote[] };
export type SetReportAction = { type: "set-report"; value: string };
export type LoadReportAction = { type: "load-report" };
export type LoadScanHitsAction = { type: "load-scan-hits" };
export type SetScanHitsAction = { type: "set-scan-hits"; value: Note[] };
export type LoadLibraryNotesAction = { type: "load-library-notes" };
export type SetLibraryNotesAction = {
  type: "set-library-notes";
  value: LibraryNoteTeaser[];
};
export type SetLibraryNotesErrorAction = {
  type: "set-library-notes-error";
  error: string;
};

// Auth actions
export type SetAuthorizedUserAction = {
  type: "set-authorized-user";
  value: Session | undefined;
};
export type SignInAction = { type: "sign-in"; email: string };
export type AuthOtpSentAction = { type: "auth-otp-sent" };
export type SignUpAction = {
  type: "sign-up";
  email: string;
  password: string;
};
export type SignOutAction = { type: "sign-out" };
export type AuthErrorAction = { type: "auth-error"; value: string };
export type ClearAuthErrorAction = { type: "clear-auth-error" };
export type ErrorAction = {
  type: "error";
  message: string;
  error: string;
};

export type AiChatAction = {
  type: "ai-chat";
  action: AiChat.Action;
};

export type ScheduleLoadNoteListsAction = {
  type: "schedule-load-note-lists";
};

export type LoadNoteListsAction = {
  type: "load-note-lists";
  value: number;
};

export type EditNoteAction = {
  type: "edit-note";
  slug: Slug;
};

export type Action =
  | ViewHomeAction
  | ViewProjectAction
  | ViewProjectConversationAction
  | ViewLibraryAction
  | ViewLibraryNoteAction
  | SetLibraryNoteViewAction
  | LoadLibraryNoteAction
  | SetLibraryNoteAction
  | SetLibraryNoteErrorAction
  | SetProjectInfoAction
  | SetProjectTitleAction
  | SetProjectDescriptionAction
  | SetProjectFocusAction
  | ScheduleProjectSaveAction
  | ProjectSaveAction
  | ProjectSaveSuccessAction
  | SetHomeViewAction
  | SetLibraryViewAction
  | SetProjectViewAction
  | SetProjectConversationViewAction
  | SetCanvasViewAction
  | ToggleAppPanelAction
  | SetSidebarOpenAction
  | ToggleCreateProjectModalAction
  | SetCreateProjectModalTitleAction
  | SetCreateProjectModalDescriptionAction
  | SubmitCreateProjectModal
  | ToggleNoteEditModalAction
  | EscKeyAction
  | ForceEditSaveSuccessAction
  | DeleteNoteAction
  | CreateConversationAction
  | CreateConversationSuccessAction
  | ReloadProjectListAction
  | SetProjectListAction
  | CreateProjectAction
  | CreateProjectSuccessAction
  | ToggleDeleteProjectAlertAction
  | DeleteProjectAction
  | DeleteProjectSuccessAction
  | DeleteConversationAction
  | DeleteConversationSuccessAction
  | LoadProjectAction
  | LoadConversationAction
  | SetConversationsAction
  | LoadForcesAction
  | SetForcesAction
  | SetForceConnectionsAction
  | AppendForcesAction
  | SetReportAction
  | LoadReportAction
  | LoadLibraryNotesAction
  | SetLibraryNotesAction
  | SetLibraryNotesErrorAction
  | SetAuthorizedUserAction
  | SignInAction
  | SignUpAction
  | SignOutAction
  | AuthErrorAction
  | AuthOtpSentAction
  | ClearAuthErrorAction
  | ErrorAction
  | AiChatAction
  | EditNoteAction
  | LoadScanHitsAction
  | SetScanHitsAction
  | ScheduleLoadNoteListsAction
  | LoadNoteListsAction
  | SetScenarioPhaseAction;

export type Model = {
  loading: boolean;
  session: Session | undefined;
  authError: string | undefined;
  otpSent: boolean;
  projectSaveToken: number;
  view: AppViewKey;
  /** Ephemeral canvas view shown alongside conversation */
  canvasView: CanvasViewKey | undefined;
  /** Current project ID (flattened from view for ergonomic access) */
  projectId: Ulid | undefined;
  /** True while project data is being fetched */
  isProjectLoading: boolean;
  /** Current conversation ID (flattened from view for ergonomic access) */
  conversationId: Ulid | undefined;
  /** Current note slug (flattened from view for ergonomic access) */
  slug: Slug | undefined;
  projectDescription: string;
  projectTitle: string;
  projectPhase: string | undefined;
  scenarioPhase: ScenarioPhase | undefined;
  projectFocus: string;
  projects: ProjectItem[];
  conversations: Conversation[];
  showPanel: boolean;
  sidebarOpen: boolean;
  showCreateProjectModal: boolean;
  isEditNoteModalOpen: boolean;
  createProjectModalTitle: string;
  createProjectModalDescription: string;
  forces: Map<Slug, ForceNote>;
  forceConnections: Map<Slug, ForceConnectionNote>;
  scanHits: Map<Slug, Note>;
  report: string;
  /** Main agent ID for current project */
  projectAgentId: Ulid | undefined;
  /** AI chat state (scoped store) */
  aiChat: AiChat.Model;
  noteEditSlug: string | undefined;
  libraryNoteItems: Map<Slug, LibraryNoteItem>;
  libraryNotesLoading: boolean;
  libraryNotesError: string | undefined;
  libraryNote: LibraryNote | undefined;
  libraryNoteLoading: boolean;
  libraryNoteError: string | undefined;
  showDeleteProjectAlert: boolean;
  /** Tool call ID of the last auto-activated view (deduplication) */
  activatedViewToolCallId: string | undefined;
  /** Token for debouncing note list loads triggered by agent saveNote */
  loadNoteListsToken: number;
  /** Last canvas view that was open (for toggle-reopen) */
  lastCanvasView: CanvasViewKey | undefined;
  /** Last canvas slug that was open (for toggle-reopen) */
  lastCanvasSlug: Slug | undefined;
};

export const model = ({
  session = undefined,
  loading = true,
  authError = undefined,
  otpSent = false,
  projectSaveToken = 0,
  view = "home",
  canvasView = undefined,
  projectId = undefined,
  isProjectLoading = false,
  conversationId = undefined,
  slug = undefined,
  projectDescription = "",
  projectTitle = "",
  projectPhase = undefined,
  scenarioPhase = undefined,
  projectFocus = "",
  projects = [],
  conversations = [],
  showPanel = true,
  sidebarOpen = true,
  showCreateProjectModal = false,
  isEditNoteModalOpen = false,
  createProjectModalTitle = "",
  createProjectModalDescription = "",
  forces = new Map(),
  forceConnections = new Map(),
  scanHits = new Map(),
  report = "",
  projectAgentId = undefined,
  aiChat = AiChat.model(),
  noteEditSlug = undefined,
  libraryNoteItems = new Map(),
  libraryNotesLoading = false,
  libraryNotesError = undefined,
  libraryNote = undefined,
  libraryNoteLoading = false,
  libraryNoteError = undefined,
  showDeleteProjectAlert = false,
  activatedViewToolCallId = undefined,
  loadNoteListsToken = 0,
  lastCanvasView = undefined,
  lastCanvasSlug = undefined,
}: Partial<Model> = {}): Model => ({
  session,
  loading,
  authError,
  otpSent,
  projectSaveToken,
  view,
  canvasView,
  projectId,
  isProjectLoading,
  conversationId,
  slug,
  projectDescription,
  projectTitle,
  projectPhase,
  scenarioPhase,
  projectFocus,
  projects,
  conversations,
  showPanel,
  sidebarOpen,
  showCreateProjectModal,
  isEditNoteModalOpen,
  createProjectModalTitle,
  createProjectModalDescription,
  forces,
  forceConnections,
  scanHits,
  report,
  projectAgentId,
  aiChat,
  noteEditSlug,
  libraryNoteItems,
  libraryNotesLoading,
  libraryNotesError,
  libraryNote,
  libraryNoteLoading,
  libraryNoteError,
  showDeleteProjectAlert,
  activatedViewToolCallId,
  loadNoteListsToken,
  lastCanvasView,
  lastCanvasSlug,
});

export const isProjectLoading = (state: Model): boolean =>
  state.isProjectLoading || state.projectId === undefined;

/** Convert model into an upsert description */
export const toProjectInfo = (state: Model): ProjectInfo | undefined => {
  if (state.projectId === undefined) return undefined;

  return {
    id: state.projectId,
    title: state.projectTitle,
    description: state.projectDescription,
    phase: state.projectPhase ?? null,
    focus: state.projectFocus,
  };
};

export const updateSetAuthorizedUser = (
  state: Model,
  session: Session | undefined,
): Model => ({
  ...state,
  session,
  loading: false,
});

export const updateAuthError = (state: Model, error: string): Model => ({
  ...state,
  authError: error,
  loading: false,
});

export const updateClearAuthError = (state: Model): Model => ({
  ...state,
  authError: undefined,
});

/** Reset all project-scoped fields to defaults, preserving app-level state. */
export const clearProject = (state: Model): Model => ({
  ...state,
  projectId: undefined,
  isProjectLoading: true,
  conversationId: undefined,
  slug: undefined,
  projectTitle: "",
  projectDescription: "",
  projectPhase: undefined,
  scenarioPhase: undefined,
  projectFocus: "",
  projectAgentId: undefined,
  projectSaveToken: 0,
  conversations: [],
  forces: new Map(),
  forceConnections: new Map(),
  scanHits: new Map(),
  report: "",
  canvasView: undefined,
  aiChat: AiChat.model(),
  isEditNoteModalOpen: false,
  noteEditSlug: undefined,
  showDeleteProjectAlert: false,
  activatedViewToolCallId: undefined,
  loadNoteListsToken: 0,
});

export const updateSetHomeView = (state: Model): Model => ({
  ...clearProject(state),
  view: "home",
});

export const updateSetLibraryView = (state: Model): Model => ({
  ...clearProject(state),
  view: "library",
  libraryNotesLoading: true,
  libraryNotesError: undefined,
});

export const updateSetProjectView = (
  state: Model,
  action: SetProjectViewAction,
): Model => ({
  ...state,
  view: "project",
  projectId: action.projectId,
  conversationId: undefined,
  slug: undefined,
});

export const updateSetProjectConversationView = (
  state: Model,
  action: SetProjectConversationViewAction,
): Model => ({
  ...state,
  view: "project-conversation",
  projectId: action.projectId,
  conversationId: action.conversationId,
  slug: undefined,
});

export const updateSetCanvasView = (
  state: Model,
  action: SetCanvasViewAction,
): Model => ({
  ...state,
  canvasView: action.canvasView,
  slug: action.slug,
  sidebarOpen: action.canvasView !== undefined ? false : true,
  activatedViewToolCallId: action.toolCallId ?? state.activatedViewToolCallId,
  lastCanvasView: action.canvasView ?? state.lastCanvasView,
  lastCanvasSlug:
    action.canvasView !== undefined ? action.slug : state.lastCanvasSlug,
});

/** Map tool viewId to a CanvasViewKey */
export const activateCanvasViewKey = (
  viewId: string | undefined,
): CanvasViewKey | undefined => {
  switch (viewId) {
    case "force-table":
      return "force-table";
    case "force-vis":
      return "force-vis";
    case "force-map":
      return "force-map";
    case "project-info":
      return "project-info";
    case "report":
      return "report";
    case "scan-hits":
      return "scan-hits";
    default:
      return undefined;
  }
};

/** Map scenario_phase to a CanvasViewKey */
export const canvasViewForPhase = (
  phase: string | undefined,
): CanvasViewKey | undefined => {
  switch (phase) {
    case "identify-forces":
      return "force-table";
    case "connect-forces":
      return "force-vis";
    case "map-forces":
      return "force-map";
    case "strategic-report":
      return "report";
    case "scan-environment":
      return "scan-hits";
    default:
      return undefined;
  }
};

export const updateSetProjectInfo = (
  state: Model,
  info: ProjectInfo,
): Model => ({
  ...state,
  isProjectLoading: false,
  projectTitle: info.title,
  projectDescription: info.description,
  projectPhase: info.phase ?? undefined,
  projectFocus: info.focus,
  projectAgentId: info.agent_id,
});

export const updateSetProjectTitle = (state: Model, title: string): Model => ({
  ...state,
  projectTitle: title,
});

export const updateSetProjectDescription = (
  state: Model,
  projectDescription: string,
): Model => ({
  ...state,
  projectDescription,
});

export const updateSetProjectFocus = (state: Model, focus: string): Model => ({
  ...state,
  projectFocus: focus,
});

export const updateScheduleProjectSave = (state: Model): Model => ({
  ...state,
  projectSaveToken: Date.now(),
});

export const updateSetProjectList = (
  state: Model,
  projects: ProjectItem[],
): Model => ({
  ...state,
  projects,
});

export const updateSetConversations = (
  state: Model,
  conversations: Conversation[],
): Model => ({
  ...state,
  conversations,
});

export const updateSetForces = (state: Model, forces: ForceNote[]): Model => ({
  ...state,
  forces: toMapBySlug(forces),
});

export const updateSetScanHits = (state: Model, scanHits: Note[]): Model => ({
  ...state,
  scanHits: toMapBySlug(scanHits),
});

export const updateSetForceConnections = (
  state: Model,
  connections: ForceConnectionNote[],
): Model => ({
  ...state,
  forceConnections: toMapBySlug(connections),
});

export const updateSetLibraryNotes = (
  state: Model,
  notes: LibraryNoteTeaser[],
): Model => {
  const items: LibraryNoteItem[] = notes.map((note) => ({
    slug: note.slug,
    type: note.type,
    title: note.title,
    excerpt: note.excerpt,
    tags: note.tags,
    attachments: note.attachments,
  }));
  return {
    ...state,
    libraryNoteItems: toMapBySlug(items),
    libraryNotesLoading: false,
    libraryNotesError: undefined,
  };
};

export const updateLoadLibraryNotes = (state: Model): Model => ({
  ...state,
  libraryNotesLoading: true,
  libraryNotesError: undefined,
});

export const updateSetLibraryNotesError = (
  state: Model,
  error: string,
): Model => ({
  ...state,
  libraryNotesLoading: false,
  libraryNotesError: error,
});

export const updateSetLibraryNoteView = (
  state: Model,
  action: SetLibraryNoteViewAction,
): Model => ({
  ...state,
  view: "library-note",
  slug: action.slug,
  projectId: undefined,
  conversationId: undefined,
  libraryNote: undefined,
  libraryNoteLoading: true,
  libraryNoteError: undefined,
});

export const updateSetLibraryNote = (
  state: Model,
  value: LibraryNote | undefined,
): Model => ({
  ...state,
  libraryNote: value,
  libraryNoteLoading: false,
  libraryNoteError: undefined,
});

export const updateSetLibraryNoteError = (
  state: Model,
  error: string,
): Model => ({
  ...state,
  libraryNoteLoading: false,
  libraryNoteError: error,
});

export const updateAppendForces = (
  state: Model,
  forces: ForceNote[],
): Model => ({
  ...state,
  forces: updateMapBySlug(state.forces, forces),
});

export const updateSetReport = (state: Model, report: string): Model => ({
  ...state,
  report,
});

export const updateToggleAppPanel = (state: Model, show: boolean): Model => ({
  ...state,
  showPanel: show,
});

export const updateSetSidebarOpen = (state: Model, open: boolean): Model => ({
  ...state,
  sidebarOpen: open,
});

export const updateToggleCreateProjectModal = (
  state: Model,
  showCreateProjectModal: boolean,
): Model => ({
  ...state,
  showCreateProjectModal,
  ...(showCreateProjectModal
    ? {}
    : {
        createProjectModalTitle: "",
        createProjectModalDescription: "",
      }),
});

export const updateSetCreateProjectModalTitle = (
  state: Model,
  title: string,
): Model => ({
  ...state,
  createProjectModalTitle: title,
});

export const updateSetCreateProjectModalDescription = (
  state: Model,
  description: string,
): Model => ({
  ...state,
  createProjectModalDescription: description,
});

export const updateSubmitCreateProjectModal = (
  state: Model,
  _action: SubmitCreateProjectModal,
): Model => ({
  ...state,
  showCreateProjectModal: false,
  createProjectModalTitle: "",
  createProjectModalDescription: "",
});

export const updateToggleNoteEditModal = (
  state: Model,
  action: ToggleNoteEditModalAction,
): Model => ({
  ...state,
  isEditNoteModalOpen: action.value,
  noteEditSlug: action.value ? state.noteEditSlug : undefined,
});

export const updateCreateProjectSuccess = (
  state: Model,
  info: ProjectInfo,
): Model => ({
  ...state,
  projects: dedupeById([info, ...state.projects]),
});

export const updateToggleDeleteProjectAlert = (
  state: Model,
  show: boolean,
): Model => ({
  ...state,
  showDeleteProjectAlert: show,
});

export const updateDeleteProjectSuccess = (state: Model, id: Ulid): Model => ({
  ...state,
  showDeleteProjectAlert: false,
  projects: state.projects.filter((p) => p.id !== id),
});

export const updateDeleteConversationSuccess = (
  state: Model,
  conversationId: Ulid,
): Model => ({
  ...state,
  conversations: state.conversations.filter((c) => c.id !== conversationId),
});

export const updateViewProjectConversation = (
  state: Model,
  action: ViewProjectConversationAction,
): Model => {
  const session = Result.toOption(action.session);
  return {
    ...state,
    view: "project-conversation",
    projectId: action.projectId,
    conversationId: action.conversationId,
    slug: undefined,
    scenarioPhase: undefined,
    loading: false,
    session,
  };
};

export const updateEscKey = (state: Model): Model => {
  return state;
};

export const updateCreateConversationSuccess = (
  state: Model,
  action: CreateConversationSuccessAction,
): Model => ({
  ...state,
  conversations: [
    {
      id: action.conversationId,
      project_id: action.projectId,
      agent_id: action.agentId,
      title: action.title,
      created_at: action.createdAt,
      updated_at: action.updatedAt,
    },
    ...state.conversations,
  ],
});

export const updateAiChat = (state: Model, action: AiChat.Action): Model => {
  const next = {
    ...state,
    aiChat: AiChat.update(state.aiChat, action),
  };
  if (action.type === "set-conversation-info") {
    return {
      ...next,
      conversations: next.conversations.map((c) => {
        // Update conversation title for matching conversation
        if (c.id === next.aiChat.conversationId) {
          return { ...c, title: action.title };
        } else {
          return c;
        }
      }),
    };
  }
  return next;
};

export const updateEditNote = (
  state: Model,
  action: EditNoteAction,
): Model => ({
  ...state,
  noteEditSlug: action.slug,
  isEditNoteModalOpen: true,
});

export const update = (state: Model, action: Action): Model => {
  switch (action.type) {
    case "set-authorized-user":
      return updateSetAuthorizedUser(state, action.value);
    case "auth-error":
      return updateAuthError(state, action.value);
    case "clear-auth-error":
      return updateClearAuthError(state);
    case "auth-otp-sent":
      return { ...state, otpSent: true, loading: false };
    case "set-home-view":
      return updateSetHomeView(state);
    case "set-library-view":
      return updateSetLibraryView(state);
    case "set-project-view":
      return updateSetProjectView(state, action);
    case "set-project-conversation-view":
      return updateSetProjectConversationView(state, action);
    case "set-canvas-view":
      return updateSetCanvasView(state, action);
    case "set-project-info":
      return updateSetProjectInfo(state, action.value);
    case "set-project-title":
      return updateSetProjectTitle(state, action.value);
    case "set-project-description":
      return updateSetProjectDescription(state, action.value);
    case "set-project-focus":
      return updateSetProjectFocus(state, action.value);
    case "schedule-project-save":
      return updateScheduleProjectSave(state);
    case "set-project-list":
      return updateSetProjectList(state, action.value);
    case "set-conversations":
      return updateSetConversations(state, action.value);
    case "set-forces":
      return updateSetForces(state, action.value);
    case "set-force-connections":
      return updateSetForceConnections(state, action.value);
    case "set-scan-hits":
      return updateSetScanHits(state, action.value);
    case "load-scan-hits":
      return state;
    case "load-library-notes":
      return updateLoadLibraryNotes(state);
    case "set-library-notes":
      return updateSetLibraryNotes(state, action.value);
    case "set-library-notes-error":
      return updateSetLibraryNotesError(state, action.error);
    case "set-library-note-view":
      return updateSetLibraryNoteView(state, action);
    case "set-library-note":
      return updateSetLibraryNote(state, action.value);
    case "set-library-note-error":
      return updateSetLibraryNoteError(state, action.error);
    case "append-forces":
      return updateAppendForces(state, action.value);
    case "set-report":
      return updateSetReport(state, action.value);
    case "toggle-app-panel":
      return updateToggleAppPanel(state, action.value);
    case "set-sidebar-open":
      return updateSetSidebarOpen(state, action.value);
    case "toggle-create-project-modal":
      return updateToggleCreateProjectModal(state, action.value);
    case "set-create-project-modal-title":
      return updateSetCreateProjectModalTitle(state, action.value);
    case "set-create-project-modal-description":
      return updateSetCreateProjectModalDescription(state, action.value);
    case "submit-create-project-modal":
      return updateSubmitCreateProjectModal(state, action);
    case "toggle-note-edit-modal":
      return updateToggleNoteEditModal(state, action);
    case "esc-key":
      return updateEscKey(state);
    case "toggle-delete-project-alert":
      return updateToggleDeleteProjectAlert(state, action.show);
    case "delete-project":
      return state;
    case "delete-project-success":
      return updateDeleteProjectSuccess(state, action.value);
    case "sign-in":
      return state;
    case "sign-up":
      return state;
    case "sign-out":
      return state;
    case "view-home":
      return state;
    case "view-project":
      return state;
    case "view-project-conversation":
      return updateViewProjectConversation(state, action);
    case "view-library":
      return state;
    case "view-library-note":
      return state;
    case "load-library-note":
      return state;
    case "project-save":
      return state;
    case "project-save-success":
      return state;
    case "force-edit-save-success":
      return state;
    case "delete-note":
      return state;
    case "reload-project-list":
      return state;
    case "create-project":
      return state;
    case "create-project-success":
      return updateCreateProjectSuccess(state, action.info);
    case "create-conversation":
      return state;
    case "create-conversation-success":
      return updateCreateConversationSuccess(state, action);
    case "ai-chat":
      return updateAiChat(state, action.action);
    case "load-project":
      return { ...clearProject(state), projectId: action.id };
    case "load-conversation":
      return state;
    case "delete-conversation":
      return state;
    case "delete-conversation-success":
      return updateDeleteConversationSuccess(state, action.value);
    case "load-forces":
      return state;
    case "load-report":
      return state;
    case "error":
      return state;
    case "edit-note":
      return updateEditNote(state, action);
    case "schedule-load-note-lists":
      return { ...state, loadNoteListsToken: state.loadNoteListsToken + 1 };
    case "load-note-lists":
      return state;
    case "set-scenario-phase":
      if (state.scenarioPhase === action.value) return state;
      return { ...state, scenarioPhase: action.value };
    default:
      return updateUnknown(state, action);
  }
};

type GetState = () => Model;

async function* fxSignIn(
  _getState: GetState,
  email: string,
): AsyncGenerator<Action> {
  const { error } = await supabase.auth.signInWithOtp({ email });

  if (error) {
    yield { type: "auth-error", value: error.message };
    return;
  }

  yield { type: "auth-otp-sent" };
}

async function* fxSignUp(
  _getState: GetState,
  email: string,
  password: string,
): AsyncGenerator<Action> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });

  if (error) {
    yield { type: "auth-error", value: error.message };
    return;
  }

  if (!data.session) {
    yield { type: "auth-error", value: "User not found" };
    return;
  }

  yield { type: "set-authorized-user", value: data.session };
}

async function* fxSignOut(_getState: GetState): AsyncGenerator<Action> {
  const { error } = await supabase.auth.signOut();
  if (error) {
    yield { type: "auth-error", value: "Failed to sign out" };
    return;
  }
  yield { type: "set-authorized-user", value: undefined };
}

async function* fxViewHome(
  _getState: GetState,
  action: ViewHomeAction,
): AsyncGenerator<Action> {
  const session = Result.toOption(action.session);
  if (session === undefined) {
    yield { type: "sign-out" };
    return;
  }

  yield { type: "set-authorized-user", value: session };
  yield { type: "set-home-view" };
}

async function* fxViewLibrary(
  _getState: GetState,
  action: ViewLibraryAction,
): AsyncGenerator<Action> {
  const session = Result.toOption(action.session);
  if (session === undefined) {
    yield { type: "sign-out" };
    return;
  }

  yield { type: "set-authorized-user", value: session };
  yield { type: "set-library-view" };
  yield { type: "load-library-notes" };
}

async function* fxViewProject(
  _getState: GetState,
  action: ViewProjectAction,
): AsyncGenerator<Action> {
  const session = Result.toOption(action.session);
  if (session === undefined) {
    return yield { type: "sign-out" };
  }

  const projectId = action.projectId;
  if (projectId === undefined) {
    log.warn("No project set. Redirecting...");
    return await router.navigate(Config.BASE_PATH);
  }

  yield { type: "set-authorized-user", value: session };
  yield {
    type: "load-project",
    id: projectId,
    accessToken: session.access_token,
    force: true,
  };
  yield { type: "set-project-view", projectId };
}

async function* fxViewProjectConversation(
  getState: GetState,
  action: ViewProjectConversationAction,
): AsyncGenerator<Action> {
  yield { type: "reload-project-list" };

  // Load project info so projectTitle is available for breadcrumb
  yield {
    type: "load-project",
    id: action.projectId,
    accessToken: getState().session?.access_token,
    force: true,
  };

  // Set conversation, optionally loading messages from API
  yield {
    type: "ai-chat",
    action: {
      type: "set-conversation",
      conversationId: action.conversationId,
      projectId: action.projectId,
      load: action.loadMessages ?? true,
      accessToken: getState().session?.access_token,
    },
  };

  // Fetch initial scenario phase from _state note
  const accessToken = getState().session?.access_token;
  if (accessToken) {
    const noteResult = await NotesService.getNote(
      accessToken,
      action.projectId,
      "_state",
    );
    if (!noteResult.ok) {
      log.warn("Failed to fetch _state note", { error: noteResult.error });
    } else if (!noteResult.value) {
      log.info("No _state note found for project", {
        projectId: action.projectId,
      });
    } else {
      const meta = noteResult.value.meta as Record<string, unknown>;
      const phase =
        typeof meta?.scenario_phase === "string"
          ? (meta.scenario_phase as ScenarioPhase)
          : undefined;
      yield { type: "set-scenario-phase", value: phase };
      const canvasView = canvasViewForPhase(phase);
      if (canvasView) {
        yield { type: "set-canvas-view", canvasView, slug: undefined };
      }
    }
  }
}

async function* fxScheduleProjectSave(
  saveToken: number,
): AsyncGenerator<Action> {
  // Wait for debounce period
  await new Promise((resolve) => setTimeout(resolve, 2000));

  yield { type: "project-save", value: saveToken };
}

async function* fxProjectSave(
  getState: GetState,
  token: number,
): AsyncGenerator<Action> {
  const state = getState();

  // Ignore if a newer save was scheduled
  if (state.projectSaveToken > token) {
    return;
  }

  const project = toProjectInfo(state);
  if (project === undefined) {
    log.warn("Could not save project. Project is undefined.");
    return;
  }

  const result = await upsertProject(state.session?.access_token, project);
  if (!result.ok) {
    return {
      type: "error",
      message: "Could not save project",
      error: result.error,
    };
  }

  yield { type: "project-save-success" };
}

async function* fxDeleteNote(
  slug: Slug,
  projectId: Ulid,
  redirectTo: string | undefined,
  accessToken: string | undefined,
): AsyncGenerator<Action> {
  if (!accessToken) {
    log.warn("Can't delete note. No access token.");
    return;
  }

  const result = await NotesService.deleteNote(accessToken, projectId, slug);

  if (!result.ok) {
    log.info("Couldn't delete note", { error: result.error });
    yield {
      type: "error",
      message: "Couldn't delete note",
      error: result.error,
    };
    return;
  }

  yield { type: "schedule-load-note-lists" };
  yield { type: "toggle-note-edit-modal", value: false };

  if (redirectTo) {
    await router.navigate(redirectTo);
  }
}

async function* fxReloadProjectList(
  getState: GetState,
): AsyncGenerator<Action> {
  const state = getState();
  const result = await getProjects(state.session?.access_token);
  if (!result.ok) {
    yield {
      type: "error",
      message: "Couldn't reload project list",
      error: result.error,
    };
  } else {
    yield { type: "set-project-list", value: result.value };
  }
}

async function* fxUpsertProject(
  getState: GetState,
  info: ProjectInfo,
): AsyncGenerator<Action> {
  const state = getState();
  const result = await upsertProject(state.session?.access_token, info);
  if (!result.ok) {
    return yield {
      type: "error",
      message: "Couldn't create project",
      error: result.error,
    };
  }

  yield { type: "create-project-success", info: result.value };
}

async function fxCreateProjectSuccess(
  _getState: GetState,
  info: ProjectInfo,
): Promise<void> {
  const projectId = info.id;

  await router.navigate(Config.PROJECT_URL, {
    params: { project: projectId },
  });
}

async function* fxCreateConversation(
  getState: GetState,
  action: CreateConversationAction,
): AsyncGenerator<Action> {
  const state = getState();
  const accessToken = state.session?.access_token;

  if (!accessToken) {
    return yield {
      type: "error",
      message: "Cannot create conversation",
      error: "No access token",
    };
  }

  const projectId = state.projectId;
  if (!projectId) {
    return yield {
      type: "error",
      message: "Cannot create conversation",
      error: "No project selected",
    };
  }

  const agentId = state.projectAgentId;
  if (!agentId) {
    return yield {
      type: "error",
      message: "Cannot create conversation",
      error: "No agent found for project",
    };
  }

  const conversationId = ulid();

  const result = await createConversation(accessToken, {
    id: conversationId,
    project_id: projectId,
    agent_id: agentId,
  });

  if (!result.ok) {
    return yield {
      type: "error",
      message: "Failed to create conversation",
      error: result.error,
    };
  }

  const conversation = result.value;

  // Add conversation to list
  yield {
    type: "create-conversation-success",
    projectId,
    conversationId: conversation.id,
    agentId: conversation.agent_id,
    title: conversation.title,
    message: action.message,
    createdAt: conversation.created_at,
    updatedAt: conversation.updated_at,
  };

  // Create user message with client-generated ID
  const now = nowIsoString();
  const userMessage = parseUserChatMessage({
    role: "user",
    parts: [{ type: "text", text: action.message }],
    metadata: {
      project_id: projectId,
      conversation_id: conversation.id,
      created_at: now,
      updated_at: now,
    },
  });

  // Resume conversation with the initial message
  yield {
    type: "ai-chat",
    action: {
      type: "resume-conversation",
      conversationId: conversation.id,
      projectId,
      agentId: conversation.agent_id,
      title: conversation.title,
      messages: [],
      submitMessage: userMessage,
      accessToken,
    },
  };

  // Navigate after setting up ai-chat (skip loading since we already have the message)
  await router.navigate(Config.PROJECT_CONVERSATION_URL, {
    params: {
      project: projectId,
      conversation: conversation.id,
    },
    state: { loadMessages: false },
  });
}

async function* fxLoadProject(
  getState: GetState,
  action: LoadProjectAction,
): AsyncGenerator<Action> {
  const accessToken = action.accessToken;
  if (!accessToken) {
    log.error("No access token provided");
    return;
  }

  const projectId = action.id;
  if (!projectId) {
    log.error("No project ID provided");
    return;
  }

  const state = getState();
  // Skip if we already have this project loaded (unless forced)
  if (
    state.projectId === projectId &&
    !state.isProjectLoading &&
    !action.force
  ) {
    log.debug("Project already loaded");
    return;
  }

  const projectResult = await getProject(accessToken, projectId);

  if (!projectResult.ok) {
    log.error("Failed to load project info", { error: projectResult.error });
    return;
  }

  const conversationsResult = await listProjectConversations(
    accessToken,
    projectId,
  );

  if (!conversationsResult.ok) {
    log.error("Unable to load conversations for project", {
      error: conversationsResult.error,
    });
    return yield {
      type: "error",
      message: "Unable to load conversations for project",
      error: conversationsResult.error,
    };
  }

  yield { type: "set-project-info", value: projectResult.value };
  yield { type: "set-conversations", value: conversationsResult.value };
  yield { type: "load-forces" };
  yield { type: "load-scan-hits" };
  yield { type: "load-report" };
}

async function* fxLoadForces(getState: GetState): AsyncGenerator<Action> {
  const state = getState();
  const projectId = state.projectId;

  if (projectId === undefined) {
    log.warn("Can't reload forces. Project ID is undefined.");
    return;
  }

  const notesResult = await NotesService.searchNotes({
    token: state.session?.access_token,
    projectId: projectId,
    query: {
      type: "force",
      limit: 256,
    },
  });

  if (!notesResult.ok) {
    log.warn("Can't reload forces", { error: notesResult.error });

    yield {
      error: "Unable to load forces",
      message: notesResult.error,
    } as Action;

    return;
  }

  const forceNotesArray = Array.from(
    filterMap(notesResult.value, (note) => {
      try {
        return CoerceForceNoteSchema.parse(note);
      } catch (error) {
        log.warn("Failed to parse force note", {
          note,
          error,
        });
        return undefined;
      }
    }),
  );

  yield { type: "set-forces", value: forceNotesArray };

  // Load force connections
  const connectionsResult = await NotesService.searchNotes({
    token: state.session?.access_token,
    projectId: projectId,
    query: {
      type: "force-connection",
      limit: 256,
    },
  });

  if (!connectionsResult.ok) {
    log.warn("Can't load force connections", {
      error: connectionsResult.error,
    });
    return;
  }

  const connectionNotesArray = Array.from(
    filterMap(connectionsResult.value, (note) => {
      try {
        return CoerceForceConnectionNoteSchema.parse(note);
      } catch (error) {
        log.warn("Failed to parse force connection note", {
          note,
          error,
        });
        return undefined;
      }
    }),
  );

  yield { type: "set-force-connections", value: connectionNotesArray };
}

async function* fxLoadScanHits(getState: GetState): AsyncGenerator<Action> {
  const state = getState();
  const projectId = state.projectId;
  if (projectId === undefined) {
    log.warn("Can't reload scan hits. Project ID is undefined.");
    return;
  }
  const notesResult = await NotesService.searchNotes({
    token: state.session?.access_token,
    projectId,
    query: { type: "scan-hit" },
  });
  if (!notesResult.ok) {
    log.warn("Can't reload scan hits", { error: notesResult.error });
    return;
  }
  yield { type: "set-scan-hits", value: notesResult.value };
}

async function* fxLoadReport(getState: GetState): AsyncGenerator<Action> {
  const state = getState();
  const projectId = state.projectId;

  if (projectId === undefined) {
    log.warn("Can't reload report. Project ID is undefined.");
    return;
  }

  const noteResult = await NotesService.getNote(
    state.session?.access_token,
    projectId,
    "report",
  );

  if (!noteResult.ok) {
    return yield {
      type: "error",
      message: "Couldn't load report",
      error: noteResult.error,
    };
  }

  yield { type: "set-report", value: noteResult.value?.content ?? "" };
}

async function* fxLoadLibraryNotes(getState: GetState): AsyncGenerator<Action> {
  const state = getState();

  try {
    const noteArray = Result.unwrap(
      await LibraryNotesService.searchLibraryNotes({
        token: state.session?.access_token,
        type: "scan-hit",
      }),
    );
    yield { type: "set-library-notes", value: noteArray };
  } catch (error) {
    const errorMessage = (error as Error).message;
    yield {
      type: "set-library-notes-error",
      error: errorMessage,
    };
    yield {
      type: "error",
      message: "Failed to load library notes",
      error: errorMessage,
    };
  }
}

async function* fxViewLibraryNote(
  _getState: GetState,
  action: ViewLibraryNoteAction,
): AsyncGenerator<Action> {
  const session = Result.toOption(action.session);
  if (session === undefined) {
    yield { type: "sign-out" };
    return;
  }

  yield { type: "set-authorized-user", value: session };
  yield { type: "set-library-note-view", slug: action.slug };
  yield { type: "load-library-note", slug: action.slug };
}

async function* fxLoadLibraryNote(
  getState: GetState,
  slug: Slug,
): AsyncGenerator<Action> {
  const state = getState();

  try {
    const result = await LibraryNotesService.getLibraryNote({
      token: state.session?.access_token,
      slug,
    });
    if (!result.ok) {
      yield { type: "set-library-note-error", error: result.error };
      return;
    }
    yield { type: "set-library-note", value: result.value };
  } catch (error) {
    const errorMessage = (error as Error).message;
    yield { type: "set-library-note-error", error: errorMessage };
    yield {
      type: "error",
      message: "Failed to load library note",
      error: errorMessage,
    };
  }
}

async function* fxDeleteProject(
  getState: GetState,
  projectId: Ulid,
): AsyncGenerator<Action> {
  const state = getState();
  const result = await deleteProject(state.session?.access_token, projectId);
  if (!result.ok) {
    return yield {
      type: "error",
      message: "Failed to delete project",
      error: result.error,
    };
  }
  yield { type: "delete-project-success", value: projectId };
}

async function fxDeleteProjectSuccess(
  getState: GetState,
  projectId: Ulid,
): Promise<void> {
  const state = getState();
  if (state.projectId === projectId) {
    await router.navigate(Config.BASE_PATH);
  }
}

async function* fxDeleteConversation(
  getState: GetState,
  conversationId: Ulid,
): AsyncGenerator<Action> {
  const state = getState();
  const result = await deleteConversation(
    state.session?.access_token,
    conversationId,
  );
  if (!result.ok) {
    return yield {
      type: "error",
      message: "Failed to delete conversation",
      error: result.error,
    };
  }
  yield { type: "delete-conversation-success", value: conversationId };
}

async function fxDeleteConversationSuccess(getState: GetState): Promise<void> {
  const state = getState();
  if (state.projectId) {
    // Navigate to project view after deleting conversation
    await router.navigate(Config.PROJECT_URL, {
      params: { project: state.projectId },
    });
  } else {
    await router.navigate(Config.BASE_PATH);
  }
}

export async function* fxSetAuthorizedUser(
  getState: GetState,
  action: SetAuthorizedUserAction,
): AsyncGenerator<Action> {
  if (action.value !== undefined) {
    // Only clear the project list if it's empty (first load).
    // Skipping the clear on subsequent navigations prevents a visible blink.
    if (getState().projects.length === 0) {
      yield { type: "set-project-list", value: [] };
    }
    yield { type: "reload-project-list" };
    return;
  } else {
    return yield { type: "set-project-list", value: [] };
  }
}

/** Find completed tool calls that should trigger canvas view activations. */
export function* generateCanvasViewAction(
  messages: ChatMessage[],
  activatedViewToolCallId: string | undefined,
): Generator<SetCanvasViewAction> {
  const lastMessage = messages[messages.length - 1];

  if (lastMessage === undefined) return;

  for (const part of lastMessage.parts) {
    switch (part.type) {
      case "tool-activateCanvasView": {
        if (part.toolCallId === activatedViewToolCallId) {
          continue;
        }
        const canvasView = activateCanvasViewKey(part.input?.id);
        if (canvasView) {
          yield {
            type: "set-canvas-view",
            canvasView,
            slug: undefined,
            toolCallId: part.toolCallId,
          };
        }
        break;
      }
      case "tool-updateState": {
        if (part.toolCallId === activatedViewToolCallId) {
          continue;
        }
        const canvasView = canvasViewForPhase(
          part.input?.state?.scenario_phase,
        );
        if (canvasView) {
          yield {
            type: "set-canvas-view",
            canvasView,
            slug: undefined,
            toolCallId: part.toolCallId,
          };
        }
        break;
      }
      default:
        continue;
    }
  }
}

/** Extract scenario_phase from tool-updateState calls in the last message. */
export function* generateScenarioPhaseAction(
  messages: ChatMessage[],
): Generator<SetScenarioPhaseAction> {
  const lastMessage = messages[messages.length - 1];
  if (lastMessage === undefined) return;

  for (const part of lastMessage.parts) {
    if (part.type === "tool-updateState") {
      const phase = part.input?.state?.scenario_phase;
      if (typeof phase === "string") {
        yield { type: "set-scenario-phase", value: phase };
      }
    }
  }
}

const fxAiChat = (
  getState: GetState,
  action: AiChat.Action,
): AsyncGenerator<Action> => {
  return flatMapAsync<AiChat.Action, Action>(
    AiChat.fx(() => getState().aiChat, action),
    function* (action) {
      yield { type: "ai-chat", action } as Action;

      switch (action.type) {
        case "request-load-note-lists":
          return yield { type: "schedule-load-note-lists" };
        case "stream-message":
          yield* generateCanvasViewAction(
            [action.message],
            getState().activatedViewToolCallId,
          );
          yield* generateScenarioPhaseAction([action.message]);
          return;
        case "set-messages":
          yield* generateCanvasViewAction(
            action.messages,
            getState().activatedViewToolCallId,
          );
          yield* generateScenarioPhaseAction(action.messages);
          return;
      }
    },
  );
};

async function* fxScheduleLoadNoteLists(token: number): AsyncGenerator<Action> {
  await sleep(2000);
  yield { type: "load-note-lists", value: token };
}

async function* fxLoadNoteLists(
  getState: GetState,
  token: number,
): AsyncGenerator<Action> {
  // Ignore if a newer load was scheduled
  if (getState().loadNoteListsToken > token) {
    return;
  }
  yield { type: "load-forces" };
  yield { type: "load-scan-hits" };
  yield { type: "load-report" };
}

export const fxApp = async function* (
  getState: GetState,
  action: Action,
): AsyncGenerator<Action> {
  switch (action.type) {
    case "sign-in":
      return yield* fxSignIn(getState, action.email);
    case "sign-up":
      return yield* fxSignUp(getState, action.email, action.password);
    case "sign-out":
      return yield* fxSignOut(getState);
    case "view-home":
      return yield* fxViewHome(getState, action);
    case "view-project":
      return yield* fxViewProject(getState, action);
    case "view-project-conversation":
      return yield* fxViewProjectConversation(getState, action);
    case "view-library":
      return yield* fxViewLibrary(getState, action);
    case "view-library-note":
      return yield* fxViewLibraryNote(getState, action);
    case "load-library-note":
      return yield* fxLoadLibraryNote(getState, action.slug);
    case "set-library-view":
      return yield { type: "load-library-notes" };
    case "schedule-project-save":
      return fxScheduleProjectSave(getState().projectSaveToken);
    case "set-project-title":
      return yield { type: "schedule-project-save" };
    case "set-project-focus":
      return yield { type: "schedule-project-save" };
    case "project-save":
      return yield* fxProjectSave(getState, action.value);
    case "force-edit-save-success":
      return yield { type: "load-forces" };
    case "delete-note":
      return yield* fxDeleteNote(
        action.slug,
        action.projectId,
        action.redirectTo,
        action.accessToken,
      );
    case "reload-project-list":
      return yield* fxReloadProjectList(getState);
    case "submit-create-project-modal":
      return yield {
        type: "create-project",
        info: {
          id: action.id,
          title: action.title,
          description: action.description,
          phase: null,
          focus: "",
        },
      };
    case "create-project":
      return yield* fxUpsertProject(getState, action.info);
    case "create-project-success":
      return await fxCreateProjectSuccess(getState, action.info);
    case "create-conversation":
      return yield* fxCreateConversation(getState, action);
    case "ai-chat":
      return yield* fxAiChat(getState, action.action);
    case "load-project":
      return yield* fxLoadProject(getState, action);
    case "load-forces":
      return yield* fxLoadForces(getState);
    case "load-scan-hits":
      return yield* fxLoadScanHits(getState);
    case "load-library-notes":
      return yield* fxLoadLibraryNotes(getState);
    case "load-report":
      return yield* fxLoadReport(getState);
    case "delete-project":
      return yield* fxDeleteProject(getState, action.value);
    case "delete-project-success":
      return fxDeleteProjectSuccess(getState, action.value);
    case "delete-conversation":
      return yield* fxDeleteConversation(getState, action.value);
    case "delete-conversation-success":
      return fxDeleteConversationSuccess(getState);
    case "set-authorized-user":
      return yield* fxSetAuthorizedUser(getState, action);
    case "schedule-load-note-lists":
      return yield* fxScheduleLoadNoteLists(getState().loadNoteListsToken);
    case "load-note-lists":
      return yield* fxLoadNoteLists(getState, action.value);
    case "set-canvas-view":
      if (
        action.canvasView === "force-table" ||
        action.canvasView === "force-map"
      ) {
        return yield { type: "load-forces" };
      }
      if (action.canvasView === "scan-hits") {
        return yield { type: "load-scan-hits" };
      }
      return;
    default:
      return;
  }
};

export type AppStore = Store<Model, Action>;

export const createAppStore = (initial: Model): AppStore => {
  return pipe(
    store(update, initial),
    logger({
      log: true,
      prefix: "app ",
    }),
    fx(fxApp),
  );
};

/**
 * Global app store
 *
 * We instantiate the store then expose it as a global variable, primarily so we can
 * access it from the browser console for debugging and development purposes.
 */
export const appStore = createAppStore(model());
